import { useState, useEffect, useCallback, useRef } from "react";
import html2pdf from "html2pdf.js";
import { apiCall } from "../api";
import { can } from "../config";
import { Input, Select, Btn } from "./common/FormElements";

// Instrument / Equipment History Card — QA-F-28.
//
// The card the plant fills in by hand: a header identifying the instrument, then one line
// per event in its life, every calibration and every repair, oldest first. Same header
// fields and same ten columns as the printed form, so a page from here and a page from the
// file read the same way.
//
// The lines come from the two places instrument events are already recorded — calibrations
// and maintenance — merged on the server into one timeline. Each row still knows which
// record it came from, so deleting one goes to the right place.

const EVENT_TYPES = ["Calibration", "Repair", "Breakdown", "Preventive Maintenance", "Verification"];
const RESULTS = ["Pass", "Fail", "Adjusted", "Not Applicable"];

const BLANK_ENTRY = {
  eventType: "Calibration",
  calibrationDate: "",
  problem: "Calibration Due",
  rectification: "Calibration Done",
  sparePartUsed: "",
  certificateNo: "",
  agency: "",
  nextDueDate: "",
  signedBy: "",
  result: "Pass",
  remarks: "",
};

// The header block, laid out exactly as the machine History Card lays its own out: a
// three-column grid, label above value, so a machine card and an instrument card read
// the same way.
const HEADER_FIELDS = h => [
  ["Instrument Desc.", h.description], ["Instrument Least Count", h.leastCount], ["Calibration Frequency", h.calibrationFrequency],
  ["Instrument ID", h.instrumentId], ["Instrument Range / Capacity", h.rangeCapacity], ["Month / Year of Purchase", h.purchaseMonthYear],
  ["Instrument Type", h.type], ["Instrument Location", h.location], ["Calibration Source", h.calibrationSource],
  ["Instrument Make", h.make], ["Instrument Issued To", h.issuedTo], ["Instrument Manual Availability", h.manualAvailable],
];

function EntryModal({ entry, onSave, onClose, saving }) {
  const [form, setForm] = useState(entry || BLANK_ENTRY);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isCalibration = (form.eventType || "") === "Calibration";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="font-bold text-gray-800">Add History Card Entry</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" data-testid="entry-close">&times;</button>
        </div>
        <div className="p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Select label="Description of Incident / Event" value={form.eventType}
              onChange={e => set("eventType", e.target.value)}
              options={EVENT_TYPES.map(t => ({ value: t, label: t }))} />
            <Input label="Date" type="date" value={form.calibrationDate}
              onChange={e => set("calibrationDate", e.target.value)} />
            <Input label="Problem in Device" value={form.problem}
              onChange={e => set("problem", e.target.value)} />
            <Input label="Rectification / Maintenance Detail" value={form.rectification}
              onChange={e => set("rectification", e.target.value)} />
            <Input label="Spare Part Used (If Any)" value={form.sparePartUsed}
              onChange={e => set("sparePartUsed", e.target.value)} />
            <Input label="Agency / Source" value={form.agency}
              onChange={e => set("agency", e.target.value)} />
            {/* A certificate number and a next-due date are what a calibration produces.
                They are still editable for other events, because a repair sometimes ends
                in a re-calibration, but they are not implied. */}
            <Input label={`Calibration Certificate No.${isCalibration ? "" : " (if any)"}`}
              value={form.certificateNo} onChange={e => set("certificateNo", e.target.value)} />
            <Input label="Due Date of Calibration" type="date" value={form.nextDueDate}
              onChange={e => set("nextDueDate", e.target.value)} />
            <Input label="Sign. / Std. Room Engineer" value={form.signedBy}
              onChange={e => set("signedBy", e.target.value)} />
            <Select label="Result" value={form.result} onChange={e => set("result", e.target.value)}
              options={RESULTS.map(r => ({ value: r, label: r }))} />
          </div>
          <Input label="Remarks" value={form.remarks} onChange={e => set("remarks", e.target.value)} />
          {!form.calibrationDate && (
            <p className="text-xs text-amber-600" data-testid="entry-date-hint">
              A date is needed — the card is read in date order.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t">
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn onClick={() => onSave(form)} disabled={saving || !form.calibrationDate} testId="entry-save">
            {saving ? "Saving..." : "Save Entry"}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default function InstrumentHistoryCard({ instrument, user, unitId, refreshInstruments, onBack }) {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef(null);

  const load = useCallback(async () => {
    if (!unitId || !instrument?.id) return;
    setLoading(true);
    setError("");
    try {
      setCard(await apiCall("GET", `/instruments/${unitId}/${instrument.id}/history-card`));
    } catch (err) {
      setCard(null);
      setError(err?.response?.data?.detail || err.message || "Could not load the history card");
    } finally {
      setLoading(false);
    }
  }, [unitId, instrument?.id]);

  useEffect(() => { load(); }, [load]);

  async function handleSave(form) {
    setSaving(true);
    try {
      await apiCall("POST", `/instruments/${unitId}/${instrument.id}/calibrations`, form);
      setShowAdd(false);
      await load();
      // A calibration moves the instrument's due date, which the register and the
      // calibration plan both read.
      if (refreshInstruments) await refreshInstruments();
    } catch (err) {
      alert("Could not save the entry: " + (err?.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    if (row.source !== "calibration") {
      alert("This line came from a maintenance record, not a calibration, so it cannot be removed from the card.");
      return;
    }
    if (!window.confirm(`Delete the entry dated ${row.date}?\n\nThis cannot be undone.`)) return;
    try {
      await apiCall("DELETE", `/instruments/${unitId}/${instrument.id}/calibrations/${row.id}`);
      await load();
      if (refreshInstruments) await refreshInstruments();
    } catch (err) {
      alert("Could not delete: " + (err?.response?.data?.detail || err.message));
    }
  }

  async function handleExportPDF() {
    setExporting(true);
    await new Promise(r => setTimeout(r, 300));
    const el = cardRef.current;
    if (!el) { setExporting(false); return; }
    try {
      await html2pdf().set({
        margin: [6, 4, 6, 4],
        filename: `Instrument-History-Card_${card?.header?.instrumentId || instrument.instrumentNo}_${new Date().toISOString().split("T")[0]}.pdf`,
        image: { type: "jpeg", quality: 1 },
        html2canvas: { scale: 1.2, useCORS: true, logging: false, width: el.scrollWidth, windowWidth: el.scrollWidth },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
      }).from(el).save();
    } catch {
      alert("Failed to export PDF.");
    } finally {
      setExporting(false);
    }
  }

  const canEdit = can(user, "addHistory");
  const COLUMNS = ["S. No.", "Date", "Description of Incident / Event", "Problem in Device",
    "Rectification / Maintenance Detail", "Spare Part Used", "Calibration Certificate No.",
    "Agency / Source", "Due Date of Calibration", "Sign. / Std. Room Er."];

  return (
    <div className="space-y-4" data-testid="instrument-history-card">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-blue-600 text-sm hover:underline flex items-center gap-1 no-print" data-testid="back-to-instruments">&larr; Instrument List</button>
        <div className="flex gap-2 no-print">
          <button onClick={() => window.print()} className="border border-gray-300 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-50" data-testid="history-card-print-btn">Print</button>
          <button onClick={handleExportPDF} disabled={exporting || !card}
            className="bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
            data-testid="history-card-pdf-btn">{exporting ? "Exporting..." : "Export PDF"}</button>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading history card&hellip;</div>
      ) : error ? (
        <div className="text-sm text-red-600" data-testid="history-card-error">{error}</div>
      ) : !card ? null : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" ref={cardRef}>
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b">
            <div className="flex items-center gap-3">
              <img src="/logo.jpg" alt="Logo" className="w-10 h-10 rounded-lg object-contain" />
              <div>
                <div className="font-black text-gray-900 text-base">Advanced Anmol Metcomp Pvt Ltd</div>
                <div className="font-bold text-gray-700 text-sm">Instrument / Equipment History Card</div>
              </div>
            </div>
            <div className="text-right text-xs text-gray-500 space-y-0.5">
              <div>Doc. No.: <span className="font-semibold">{card.docNo}</span></div>
              <div>Rev. No.: <span className="font-semibold">00</span></div>
              <div>Date: <span className="font-semibold">06.07.2020</span></div>
            </div>
          </div>

          <div className="grid grid-cols-3 border-b text-xs" data-testid="history-card-header">
            {HEADER_FIELDS(card.header).map(([label, value]) => (
              <div key={label} className="border-r border-b border-gray-200 px-3 py-2 last:border-r-0">
                <div className="text-gray-400 font-medium">{label}</div>
                <div className="font-semibold text-gray-800 mt-0.5">{value || "—"}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b no-print">
            <div className="flex gap-4 text-xs">
              <span className="text-gray-500">Total Entries: <strong className="text-gray-800">{card.counts.total}</strong></span>
              <span className="text-blue-600">Calibrations: <strong>{card.counts.calibrations}</strong></span>
              <span className="text-amber-600">Repairs: <strong>{card.counts.repairs}</strong></span>
            </div>
            {canEdit && (
              <button onClick={() => setShowAdd(true)}
                className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-700 whitespace-nowrap"
                data-testid="add-history-entry-btn">+ Add Entry</button>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[1000px]" data-testid="history-card-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  {COLUMNS.map(h => (
                    <th key={h} className="text-left px-2 py-2 text-gray-500 font-semibold border-r border-gray-200 last:border-r-0 align-bottom">{h}</th>
                  ))}
                  {canEdit && <th className="px-2 py-2 no-print" />}
                </tr>
              </thead>
              <tbody>
                {card.rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + (canEdit ? 1 : 0)} className="px-3 py-6 text-center text-gray-500"
                      data-testid="history-card-empty">
                      Nothing recorded for this instrument yet.
                      {canEdit && " Use + Add Entry to start the card."}
                    </td>
                  </tr>
                ) : card.rows.map(r => (
                  <tr key={`${r.source}-${r.id}`} className="border-b hover:bg-gray-50" data-testid={`history-row-${r.sn}`}>
                    <td className="px-2 py-1.5 text-gray-400 border-r border-gray-100">{String(r.sn).padStart(2, "0")}</td>
                    <td className="px-2 py-1.5 text-gray-800 whitespace-nowrap border-r border-gray-100">{r.date || "—"}</td>
                    <td className="px-2 py-1.5 border-r border-gray-100">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${r.source === "calibration" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                        {r.event}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-gray-600 border-r border-gray-100">{r.problem || "—"}</td>
                    <td className="px-2 py-1.5 text-gray-600 border-r border-gray-100">{r.rectification || "—"}</td>
                    <td className="px-2 py-1.5 text-gray-500 border-r border-gray-100">{r.sparePartUsed || "—"}</td>
                    <td className="px-2 py-1.5 font-mono text-gray-700 border-r border-gray-100">{r.certificateNo || "—"}</td>
                    <td className="px-2 py-1.5 text-gray-600 border-r border-gray-100">{r.agency || "—"}</td>
                    <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap border-r border-gray-100">{r.dueDate || "—"}</td>
                    <td className="px-2 py-1.5 text-gray-600">{r.signedBy || "—"}</td>
                    {canEdit && (
                      <td className="px-2 py-1.5 text-right whitespace-nowrap no-print">
                        <button onClick={() => handleDelete(r)}
                          className="text-xs text-red-500 hover:text-red-700 hover:underline"
                          data-testid={`history-delete-${r.sn}`}>Del</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showAdd && (
        <EntryModal entry={null} saving={saving} onSave={handleSave} onClose={() => setShowAdd(false)} />
      )}
    </div>
  );
}
